import usePagination from "~/hooks/usePagination";
import useCurrentUser from "~/hooks/user/useCurrentUser";
import useUrlParams from "~/hooks/useUrlParams";
import type { PaginationUrlParams } from "~/types";
import { toWholeCurrency } from "~/util/currency";
import { whereToAnd } from "~/util/payload";

/**
 * Interface for the parameters of the useGetPaginatedRecurringInvoices function.
 * @interface
 * @property {number} depth - The depth of the data to fetch.
 * @property {boolean} filterOrganization - Whether to filter by organization.
 * @property {string} status - The status of the invoices to fetch.
 * @property {string} leaseId - The ID of the lease.
 * @property {string} propertyId - The ID of the property.
 * @property {boolean} serialLoading - Whether to load the data serially.
 */
interface Params {
  depth: number;
  filterOrganization: boolean;
  status?: string;
  leaseId?: string;
  propertyId?: string;
  serialLoading?: boolean;
}

/**
 * Hook to fetch paginated recurring invoice data.
 * It first fetches the current user's data to get the organization ID,
 * then fetches the recurring invoice data using that ID.
 * @param {Params} params - The parameters for the hook.
 * @returns {Object} The recurring invoice data and loading state.
 */
const useGetPaginatedRecurringInvoices = ({
  depth,
  filterOrganization,
  status,
  leaseId,
  propertyId,
  serialLoading,
}: Params) => {
  const { params: urlParams } = useUrlParams();

  const thisParamSet = urlParams?.["recurring-invoices"] as PaginationUrlParams;

  const {
    data: { organization },
    isLoading: userLoading,
  } = useCurrentUser();

  const organizationId = organization?.id;

  const where = {
    ...(thisParamSet?.where || {}),
  };

  if (status) {
    where.status = {
      equals: status,
    };
  }

  if (leaseId) {
    where.lease = {
      equals: leaseId,
    };
  }

  if (propertyId) {
    where.property = {
      equals: propertyId,
    };
  }

  if (filterOrganization) {
    where.organization = {
      equals: organizationId,
    };
  }

  const { data, ...rest } = usePagination({
    collection: "recurring-invoices",
    where: whereToAnd(where),
    sort: thisParamSet?.sort,
    page: thisParamSet?.page,
    limit: thisParamSet?.limit,
    serialLoading: userLoading || serialLoading,
    depth,
  });

  const allRecurringInvoices = data?.map((invoice) => ({
    ...invoice,
    items: invoice.items.map((item) => ({
      ...item,
      amount: toWholeCurrency(item.amount),
    })),
    amountDue: toWholeCurrency(invoice.amountDue),
    amountPaid: toWholeCurrency(invoice.amountPaid),
    amountRefunded: toWholeCurrency(invoice.amountRefunded),
    total: toWholeCurrency(invoice.total),
  }));

  return {
    filterEnabled: thisParamSet?.where,
    data: allRecurringInvoices,
    ...rest,
  };
};

export default useGetPaginatedRecurringInvoices;
